<template>
    <v-card v-if="form" outlined class="mb-4 req-card" :dir="$vuetify.rtl ? 'rtl' : 'ltr'">
        <div class="sec-head">
            <v-icon small class="sec-ic">mdi-file-document-outline</v-icon>
            <span>{{ $t('SurveyRequestDetails') }}</span>
        </div>
        <div class="pa-5">
            <RequestSummaryHeader :title="form.requestTitle" :year="form.year" :period="form.period" />

            <!-- بيانات الجهة -->
            <div class="grp"><v-icon small class="grp-ic me-2">mdi-office-building-outline</v-icon>{{ $t('EntityInformation') }}</div>
            <v-row dense>
                <v-col cols="12" md="6"><kv :label="$t('AgencyOrUnitName')" :val="form.agencyName" /></v-col>
                <v-col cols="12" md="6"><kv :label="$t('ContactPersonName')" :val="form.contactName" /></v-col>
                <v-col cols="12" md="4"><kv :label="$t('JobTitle')" :val="form.jobTitle" /></v-col>
                <v-col cols="12" md="4"><kv :label="$t('Mobile')" :val="form.mobile" /></v-col>
                <v-col cols="12" md="4"><kv :label="$t('Email')" :val="form.email" /></v-col>
            </v-row>

            <!-- بيانات الاستطلاع -->
            <div class="grp"><v-icon small class="grp-ic me-2">mdi-poll</v-icon>{{ $t('SurveyInformation') }}</div>
            <v-row dense>
                <v-col cols="12" md="8"><kv :label="$t('SurveyTitleField')" :val="form.surveyTitle" /></v-col>
                <v-col cols="12" md="4"><kv :label="$t('SurveyStatus')" :val="statusMap[form.surveyStatus]" /></v-col>
                <v-col cols="12"><kv :label="$t('SurveyObjectives')" :val="form.objectives" /></v-col>
                <v-col cols="12"><kv :label="$t('SurveyType')" :chips="mapList(form.surveyTypes, typeMap)" /></v-col>
                <v-col cols="12"><kv :label="$t('SurveyImportanceImpactShort')" :val="form.importanceImpact" /></v-col>
                <v-col cols="12"><kv :label="$t('QuestionThemes')" :val="form.questionThemes" /></v-col>
            </v-row>

            <!-- الفئة المستهدفة من الاستطلاع (v-if: requests submitted before these fields existed) -->
            <template v-if="form.sampleScope">
                <div class="grp"><v-icon small class="grp-ic me-2">mdi-account-search-outline</v-icon>{{ $t('SurveyTargetAudience') }}</div>
                <kv :label="$t('TargetAudience')" :val="scopeMap[form.sampleScope] || form.sampleScope" />
                <kv v-if="form.otherSampleDescription" :label="$t('TargetAudienceDescription')" :val="form.otherSampleDescription" />
                <kv v-if="form.hasSampleContacts" :label="$t('SampleContactsAvailable')"
                    :val="yesNoMap[form.hasSampleContacts] || form.hasSampleContacts" />
            </template>

            <!-- تحديد نوع العينة المستهدفة من الاستطلاع -->
            <div class="grp"><v-icon small class="grp-ic me-2">mdi-account-group-outline</v-icon>{{ $t('TargetSampleType') }}</div>
            <kv :chips="mapList(form.demographics, demoMap)" />
            <kv v-if="form.demographicsOther" :label="$t('OtherData')" :val="form.demographicsOther" />

            <!-- قاعدة البيانات -->
            <div class="grp"><v-icon small class="grp-ic me-2">mdi-database-outline</v-icon>{{ $t('DatabaseSection') }}</div>
            <kv :chips="mapList(form.dataAvailability, dataMap)" />
            <kv v-if="form.dataAvailabilityOther" :label="$t('Other')" :val="form.dataAvailabilityOther" />

            <!-- الأسئلة المقترحة -->
            <template v-if="form.proposedQuestions && form.proposedQuestions.length">
                <div class="grp"><v-icon small class="grp-ic me-2">mdi-help-circle-outline</v-icon>{{ $t('ProposedQuestions') }}</div>
                <div class="q-wrap">
                    <!-- old requests stored plain strings; new ones store { text, type, options } -->
                    <div v-for="(q, i) in form.proposedQuestions" :key="i" class="q-item">
                        <span class="q-num">{{ i + 1 }}</span>
                        <div class="q-body">
                            <div class="d-flex align-center flex-wrap">
                                <!-- question text is editor HTML on requests since 2026-09-06, plain text before -->
                                <span class="q-txt q-html" v-html="qText(q)"></span>
                                <span v-if="qTypeName(q)" class="q-type-chip ms-2">{{ qTypeName(q) }}</span>
                            </div>
                            <div v-if="qOptions(q).length" class="q-options">
                                <span v-for="(o, oi) in qOptions(q)" :key="oi" class="kv-chip">{{ o }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </template>

            <!-- المرفقات -->
            <template v-if="attachments.length">
                <div class="grp"><v-icon small class="grp-ic me-2">mdi-paperclip</v-icon>{{ $t('Attachments') }}</div>
                <div v-for="(a, i) in attachments" :key="i" class="att-row d-flex align-center">
                    <div class="att-ic"><v-icon small :color="canView(a) ? '#e53935' : '#90a0ad'">{{ fileIcon(a) }}</v-icon></div>
                    <span class="att-name">{{ fileName(a) }}</span>
                    <v-spacer />
                    <v-btn v-if="canView(a)" x-small depressed color="primary" class="ms-2 att-btn" @click="view(a)">
                        <v-icon x-small>mdi-eye-outline</v-icon><span>{{ $t('View') }}</span>
                    </v-btn>
                    <v-btn x-small outlined color="primary" class="ms-2 att-btn" @click="download(a)">
                        <v-icon x-small>mdi-download</v-icon><span>{{ $t('Download') }}</span>
                    </v-btn>
                </div>
            </template>
        </div>
    </v-card>
</template>

<script>
    import ProcessesService from '@/services/ProcessesService.js'
    import RequestSummaryHeader from '@/components/app/process/RequestSummaryHeader.vue'
    import { normalizeAttachments } from '@/helpers/attachments.js'

    const kv = {
        name: 'kv',
        props: { label: String, val: [String, Number], chips: Array },
        render(h) {
            const children = []
            if (this.label) children.push(h('div', { class: 'kv-label' }, this.label))
            if (this.chips) {
                children.push(h('div', this.chips.length
                    ? this.chips.map(c => h('span', { class: 'kv-chip' }, c))
                    : [h('span', { class: 'kv-empty' }, '—')]))
            } else {
                children.push(h('div', { class: 'kv-val' }, this.val ? String(this.val) : '—'))
            }
            return h('div', { class: 'kv' }, children)
        }
    }

    export default {
        components: { kv, RequestSummaryHeader },
        props: {
            taskId: { type: Number, default: null },
            processInstanceId: { type: Number, default: null }
        },
        data() {
            return {
                form: null,
                attachments: [],
                statusMap: { new: this.$t('StatusNew'), previous: this.$t('StatusPreviouslyConducted') },
                scopeMap: { general: this.$t('SampleScopeGeneral'), ministry: this.$t('SampleScopeMinistry'), other: this.$t('SampleScopeOther') },
                yesNoMap: { yes: this.$t('Yes'), no: this.$t('No') },
                // okrs is no longer offered on the request form, but requests submitted before it was
                // removed still carry it — keep the label so they don't render the raw "okrs" value.
                typeMap: { okrs: this.$t('SurveyTypeOkrs'), kpis: this.$t('SurveyTypeKpis'), minister: this.$t('SurveyTypeMinister') },
                demoMap: {
                    gender: this.$t('Gender'), housing: this.$t('HousingType'), age: this.$t('AgeGroup'), activity: this.$t('ActivityType'),
                    education: this.$t('EducationalLevel'), activityYears: this.$t('ActivityYears'), municipality: this.$t('MunicipalityName'), other: this.$t('OtherData')
                },
                dataMap: { available: this.$t('DataAvailableAtRequester'), other: this.$t('Other') },
            }
        },
        watch: {
            taskId: { immediate: true, handler() { this.load() } },
            processInstanceId: { immediate: true, handler() { this.load() } }
        },
        methods: {
            load() {
                const req = this.taskId
                    ? ProcessesService.getSurveyExecutionRequestByTask(this.taskId)
                    : (this.processInstanceId ? ProcessesService.getSurveyExecutionRequestByProcess(this.processInstanceId) : null)
                if (!req) return
                this.form = null
                this.attachments = []
                req.then(res => {
                    if (res.data) {
                        this.form = res.data.form
                        this.attachments = normalizeAttachments(res.data)
                    }
                }).catch(() => { /* no linked survey-execution request */ })
            },
            mapList(arr, map) {
                return (arr || []).map(v => map[v] || v)
            },
            // proposedQuestions items are strings on old requests, objects on new ones
            qText(q) {
                return typeof q === 'string' ? q : ((q && q.text) || '')
            },
            qTypeName(q) {
                if (!q || typeof q === 'string') return ''
                const lang = this.$store.getters['user/language']
                return (lang === 'ar' ? q.typeNameAr : q.typeNameEn) || q.typeNameAr || q.typeNameEn || ''
            },
            qOptions(q) {
                return (q && typeof q !== 'string' && Array.isArray(q.options)) ? q.options : []
            },
            // a = { url: GUID storage path, name: filename the user uploaded }
            fileName(a) {
                return (a && a.name) || ''
            },
            canView(a) {
                return /\.pdf$/i.test((a && a.url) || '')
            },
            fileIcon(a) {
                const u = (a && a.url) || ''
                if (/\.pdf$/i.test(u)) return 'mdi-file-pdf-box'
                if (/\.(docx?|rtf)$/i.test(u)) return 'mdi-file-word-box'
                if (/\.xlsx?$/i.test(u)) return 'mdi-file-excel-box'
                return 'mdi-file-outline'
            },
            // Build a direct, token-authenticated URL (no Authorization header → no CORS preflight).
            attachmentUrl(a, dl) {
                const token = this.$store.getters['user/token']
                return process.env.VUE_APP_MAIN_API + 'survey-execution-requests/attachment'
                    + '?path=' + encodeURIComponent((a && a.url) || '')
                    + '&access_token=' + encodeURIComponent(token)
                    + (dl ? '&dl=true' : '')
            },
            view(a) {
                // open the inline PDF URL directly; the browser's native PDF viewer renders it
                // (the system eversuite viewer is hard-wired to its own elibrary/attachments endpoints)
                window.open(this.attachmentUrl(a, false), '_blank')
            },
            download(a) {
                // cross-origin, so the saved filename comes from the API's Content-Disposition
                const link = document.createElement('a')
                link.href = this.attachmentUrl(a, true)
                link.setAttribute('download', this.fileName(a))
                document.body.appendChild(link)
                link.click()
                link.remove()
            }
        }
    }
</script>

<style scoped>
    .req-card { border: 1px solid #e6ecea !important; border-radius: 18px !important; overflow: hidden; box-shadow: 0 1px 2px rgba(15, 23, 42, .04), 0 8px 24px -16px rgba(15, 23, 42, .18) !important; }
    .sec-head {
        display: flex; align-items: center; gap: 10px; font-weight: 800; font-size: 15px;
        padding: 14px 20px; color: #1f2a28; background: #fff; border-bottom: 1px solid #e6ecea;
    }
    .sec-head .sec-ic {
        width: 32px; height: 32px; border-radius: 10px; background: #e6f2f1; color: #0a716e !important;
        display: inline-flex; align-items: center; justify-content: center; flex: 0 0 32px;
    }

    /* section header */
    .grp {
        display: flex; align-items: center;
        font-weight: 700; font-size: 13.5px; color: #1B8354;
        margin: 24px 0 14px; padding-bottom: 9px;
        border-bottom: 2px solid #eef4f1;
    }
    .grp:first-of-type { margin-top: 2px; }
    .grp-ic { color: #1B8354 !important; }

    /* key-value tiles */
    .kv {
        background: #f7f9fb; border: 1px solid #edf1f5; border-radius: 10px;
        padding: 10px 15px; height: 100%; margin-bottom: 4px;
        transition: border-color .15s, background .15s;
    }
    .kv:hover { border-color: #d7e6df; background: #f4f9f6; }
    .kv-label { font-size: 11.5px; color: #94a3b0; margin-bottom: 4px; font-weight: 600; }
    .kv-val { font-size: 13.5px; color: #22323d; font-weight: 600; line-height: 1.75; white-space: pre-wrap; }
    .kv-chip {
        display: inline-block; background: #e9f4ef; color: #1B8354; border: 1px solid #d3ebe0;
        border-radius: 16px; padding: 3px 13px; font-size: 12px; font-weight: 600; margin: 0 0 6px 6px;
    }
    .kv-empty { color: #c2c8ce; }

    /* questions */
    .q-wrap { display: flex; flex-direction: column; gap: 8px; }
    .q-html { line-height: 1.8; }
    .q-html >>> p { margin: 0 0 4px; }
    .q-html >>> ul, .q-html >>> ol { margin: 0 0 6px; padding-inline-start: 22px; }
    .q-html >>> table { border-collapse: collapse; }
    .q-html >>> td, .q-html >>> th { border: 1px solid #dfe5ea; padding: 3px 8px; }
    .q-item {
        display: flex; align-items: flex-start; gap: 11px;
        background: #f7f9fb; border: 1px solid #edf1f5; border-radius: 10px; padding: 10px 13px;
    }
    .q-num {
        flex: 0 0 24px; width: 24px; height: 24px; border-radius: 50%;
        background: #1B8354; color: #fff; font-size: 12px; font-weight: 700;
        display: flex; align-items: center; justify-content: center; margin-top: 1px;
    }
    .q-txt { font-size: 13.5px; color: #22323d; line-height: 1.65; }
    .q-body { flex: 1 1 auto; min-width: 0; }
    .q-type-chip {
        display: inline-block; background: #eef2f7; color: #4a6076; border: 1px solid #dbe3ec;
        border-radius: 14px; padding: 1px 11px; font-size: 11.5px; font-weight: 700; white-space: nowrap;
    }
    .q-options { margin-top: 7px; }

    /* attachments */
    .att-row {
        background: #f7f9fb; border: 1px solid #edf1f5; border-radius: 10px;
        padding: 9px 15px; margin-bottom: 8px;
    }
    .att-ic {
        width: 30px; height: 30px; border-radius: 7px; background: #fff; border: 1px solid #edf1f5;
        display: flex; align-items: center; justify-content: center;
        margin-inline-end: 10px; flex: 0 0 30px;
    }
    .att-name { font-size: 13px; color: #37474f; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .att-btn { border-radius: 8px; font-weight: 600; }
    .att-btn ::v-deep .v-btn__content { gap: 5px; }
</style>
